import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'

import { queryKeys, type ParameterOptionsQueryKey } from 'api/queryKeys'

import { type ParameterOptionsRequestType } from 'hooks/useParameterOptions/useParameterOptionsTypes'
import {
  useProjectInfo,
  type ProjectInfo
} from 'hooks/useProjectInfo/useProjectInfo'

import { type ComponentInstanceId } from 'job-lib/types/Job'
import { type JobVariableCollection } from 'job-lib/types/Variables'

import { type LookUpType } from '../useGetComponentMetadata/types'
import useGetProject from '../useGetProject/useGetProject'
import { useSpringClient } from '../useSpringClient/useSpringClient'
import {
  type APIError,
  type GetParameterOptionsResponse,
  type LookupDefinition
} from './types'

const getParameterOptionsQueryKey = (
  {
    projectId,
    branchId,
    jobSummaryId
  }: Pick<ProjectInfo, 'projectId' | 'jobSummaryId' | 'branchId'>,
  lookupType: LookUpType | null,
  componentId: ComponentInstanceId | null,
  parameterId: string | undefined,
  requestType: ParameterOptionsRequestType,
  dependencies: Record<string, unknown>
) => {
  const keys: ParameterOptionsQueryKey = [
    queryKeys.parameterOptions,
    projectId,
    branchId,
    jobSummaryId,
    lookupType,
    componentId,
    parameterId,
    requestType,
    dependencies
  ]
  return keys
}

export interface UseGetParameterOptionsRequest<
  T extends ParameterOptionsRequestType
> {
  lookupDefinition?: LookupDefinition
  parameterId?: string
  lookupType: LookUpType | null
  isEnabled?: boolean
  variables: JobVariableCollection
  requestType: T
}

const getRequestMethod = (type: ParameterOptionsRequestType) => {
  switch (type) {
    case 'autofill':
    case 'autofill-nested':
      return 'autofill'
    default:
      return 'parameter-options'
  }
}

export const useGetParameterOptions = <T extends ParameterOptionsRequestType>({
  lookupDefinition,
  lookupType,
  parameterId,
  variables,
  isEnabled = false,
  requestType
}: UseGetParameterOptionsRequest<T>): UseQueryResult<
  GetParameterOptionsResponse<T> | undefined,
  APIError
> => {
  const {
    projectId,
    branchId,
    jobSummaryId,
    componentId,
    environmentId,
    agentId
  } = useProjectInfo()
  const client = useSpringClient()
  const project = useGetProject()
  const warehouse = project?.data?.warehouse?.toUpperCase() ?? ''

  const url = `/${getRequestMethod(requestType)}/${
    lookupType as LookUpType
  }?projectId=${projectId}&branchName=${branchId}&environmentId=${environmentId}&agentId=${agentId}&dataWarehouseType=${warehouse}`

  const {
    component,
    'transform.sql': transform,
    parameters,
    parameterMetadataDependencies,
    parameterIdDependencies,
    customConnectorProfileDependencies
  } = lookupDefinition ?? {}

  const dependencies = {
    ...component,
    ...parameters,
    ...parameterIdDependencies,
    ...(transform ? { 'transform.sql': transform } : {}),
    ...customConnectorProfileDependencies,
    ...(parameterMetadataDependencies ?? {})
  }

  return useQuery<GetParameterOptionsResponse<T> | undefined, APIError>(
    getParameterOptionsQueryKey(
      { projectId, branchId, jobSummaryId },
      lookupType,
      componentId,
      parameterId,
      requestType,
      dependencies
    ),
    async () => {
      try {
        const { data } = await client.post<GetParameterOptionsResponse<T>>(
          url,
          {
            // keeping for backwards compatibility. Will be removed in a subsequent ticket.
            // https://matillion.atlassian.net/browse/DPCD-735
            ...dependencies,
            variables,
            dependencies
          }
        )

        return data
      } catch (error) {
        /* istanbul ignore else */
        if (axios.isAxiosError(error)) {
          throw error.response?.data
        }
      }
    },
    { enabled: isEnabled && !!lookupType, staleTime: 10000 }
  )
}
